import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface ClockEntry {
  id: string;
  clock_in_time: string;
  clock_out_time: string | null;
  is_after_hours: boolean;
  notes: string | null;
  approved_by: string | null;
  approver_name: string | null;
  overlap_minutes: number;
}

interface ProducedSummary {
  /** oct20e LABOR: approved labor of the completed work (never the scheduling duration). */
  booked_minutes: number;
  approved_labor_minutes?: number;
  /** Actual labor: timer session minutes. */
  actual_labor_minutes?: number;
  /** The shop's stored OSH-282 pay basis; null when its compensation engine recorded none. Core computes no pay. */
  pay_basis_minutes?: number | null;
  ticket_count: number;
  wrench_minutes: number;
  session_count: number;
}

export interface PayrollEmployeeDetail {
  clock_entries: ClockEntry[];
  produced_summary: ProducedSummary;
}

interface UsePayrollEmployeeDetailParams {
  shopId?: string;
  employeeId?: string;
  employeeType?: 'technician' | 'staff';
  startDate: Date;
  endDate: Date;
  enabled?: boolean;
}

export const usePayrollEmployeeDetail = ({
  shopId,
  employeeId,
  employeeType,
  startDate,
  endDate,
  enabled = true,
}: UsePayrollEmployeeDetailParams) => {
  return useQuery({
    queryKey: ['payroll-employee-detail', shopId, employeeId, employeeType, startDate.toISOString(), endDate.toISOString()],
    queryFn: async () => {
      if (!shopId || !employeeId || !employeeType) return null;

      const { data, error } = await supabase.rpc('get_payroll_employee_detail', {
        p_shop_id: shopId,
        p_employee_id: employeeId,
        p_employee_type: employeeType,
        p_start: startDate.toISOString(),
        p_end: endDate.toISOString(),
      });

      if (error) throw error;
      return data as unknown as PayrollEmployeeDetail;
    },
    enabled: enabled && !!shopId && !!employeeId && !!employeeType,
  });
};
